import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import UpgradeNudge from "@/components/UpgradeNudge";
import {
  GraduationCap, BookOpen, Zap, ChevronRight, MessageSquare, Settings,
} from "lucide-react";

const MODES = [
  {
    to: "/learn/coach",
    icon: GraduationCap,
    title: { fr: "Coach personnel", en: "Personal coach", es: "Entrenador personal", pt: "Treinador pessoal" },
    desc: {
      fr: "Un plan d'apprentissage adaptatif semaine par semaine, avec flashcards générées sur-mesure.",
      en: "Adaptive weekly plan with bespoke flashcards.",
      es: "Plan de aprendizaje adaptativo semana a semana, con flashcards a medida.",
      pt: "Plano de aprendizagem adaptativo semanal, com flashcards personalizados.",
    },
    cost: { fr: "5 crédits / plan", en: "5 credits / plan", es: "5 créditos / plan", pt: "5 créditos / plano" },
    testId: "learn-mode-coach",
  },
  {
    to: "/learn/explain",
    icon: BookOpen,
    title: { fr: "Demande à geoVINUM AI", en: "Ask geoVINUM AI", es: "Pregunta a geoVINUM AI", pt: "Pergunte à geoVINUM AI" },
    desc: {
      fr: "Posez une question viticole et obtenez une réponse structurée en cartes, exemple et erreur courante.",
      en: "Ask any wine question, get a structured answer with cards, example, common mistakes.",
      es: "Haz cualquier pregunta sobre vino y obtén una respuesta estructurada con tarjetas, ejemplo y error común.",
      pt: "Faça qualquer pergunta sobre vinho e obtenha uma resposta estruturada com cartões, exemplo e erro comum.",
    },
    cost: { fr: "Gratuit en Découverte · 3 crédits sur les plans payants", en: "Free on Discovery · 3 credits otherwise", es: "Gratis en Descubrimiento · 3 créditos en planes de pago", pt: "Grátis em Descoberta · 3 créditos nos planos pagos" },
    testId: "learn-mode-explain",
  },
  {
    to: "/flashcards",
    icon: Zap,
    title: { fr: "Flashcards éclair", en: "Quick flashcards", es: "Flashcards rápidas", pt: "Flashcards rápidos" },
    desc: {
      fr: "10 cartes sur une thématique, retournez à votre rythme pour ancrer les bases.",
      en: "10 cards on a topic — flip at your own pace to lock in fundamentals.",
      es: "10 tarjetas sobre un tema — dale la vuelta a tu ritmo para fijar los fundamentos.",
      pt: "10 cartões sobre um tema — vire-os ao seu ritmo para fixar os fundamentos.",
    },
    cost: { fr: "2 crédits / paquet", en: "2 credits / pack", es: "2 créditos / paquete", pt: "2 créditos / pacote" },
    testId: "learn-mode-flashcards",
  },
  {
    to: "/audrey-ai",
    icon: MessageSquare,
    title: { fr: "Conversation geoVINUM AI", en: "Chat with geoVINUM AI", es: "Chat con geoVINUM AI", pt: "Conversa com geoVINUM AI" },
    desc: {
      fr: "Discussion libre avec geoVINUM AI — idéale pour tâtonner, débattre, comparer des terroirs.",
      en: "Free-form chat with geoVINUM AI — perfect to debate, compare terroirs.",
      es: "Charla libre con geoVINUM AI — ideal para debatir, comparar terruños.",
      pt: "Conversa livre com geoVINUM AI — ideal para debater, comparar terroirs.",
    },
    cost: { fr: "Inclus dans Amateur+", en: "Included in Amateur+", es: "Incluido en Amateur+", pt: "Incluído em Amateur+" },
    testId: "learn-mode-chat",
  },
];

const HEAD = {
  chip: { fr: "Révise", en: "Revise", es: "Repasa", pt: "Reveja" },
  title: { fr: "Comment voulez-vous apprendre aujourd'hui ?", en: "How do you want to learn today?", es: "¿Cómo quieres aprender hoy?", pt: "Como quer aprender hoje?" },
  configure: { fr: "Paramétrer mon microlearning", en: "Configure microlearning", es: "Configurar mi microlearning", pt: "Configurar o meu microlearning" },
  intro: {
    fr: "Choisissez votre mode — coach long-terme, explication ponctuelle, flashcards éclair ou conversation libre. geoVINUM AI s'adapte à votre niveau.",
    en: "Pick your mode — long-term coach, ad-hoc explanation, quick flashcards, or free-form chat.",
    es: "Elige tu modo — coach a largo plazo, explicación puntual, flashcards rápidas o charla libre. geoVINUM AI se adapta a tu nivel.",
    pt: "Escolha o seu modo — coach de longo prazo, explicação pontual, flashcards rápidos ou conversa livre. geoVINUM AI adapta-se ao seu nível.",
  },
  upgradeTitle: { fr: "Plus d'IA, moins de crédits", en: "More AI, fewer credits", es: "Más IA, menos créditos", pt: "Mais IA, menos créditos" },
  upgradeMsg: {
    fr: "Le plan Amateur (29 €/mois) débloque l'IA illimitée + 100 crédits/mois pour les builders.",
    en: "Amateur plan (€29/mo) unlocks unlimited AI + 100 monthly credits.",
    es: "El plan Amateur (29 €/mes) desbloquea IA ilimitada + 100 créditos mensuales.",
    pt: "O plano Amateur (29 €/mês) desbloqueia IA ilimitada + 100 créditos mensais.",
  },
};

export default function LearnHome() {
  const { user } = useAuth();
  const { lang } = useT();
  const pick = (o) => o[lang] || o.fr;

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-10 py-10 md:py-16" data-testid="learn-home">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <span className="chip-pink inline-flex items-center gap-1.5"><GraduationCap className="w-3 h-3" /> {pick(HEAD.chip)}</span>
          <h1 className="font-display text-4xl md:text-5xl text-[#1F3A5F] mt-4 leading-[1.05]">
            {pick(HEAD.title)}
          </h1>
        </div>
        <Link
          to="/microlearning"
          data-testid="learn-microlearning-link"
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full border border-[#1F3A5F]/15 text-xs font-bold text-[#1F3A5F] hover:border-[#E5276A] hover:text-[#E5276A]"
        >
          <Settings className="w-3.5 h-3.5" /> {pick(HEAD.configure)}
        </Link>
      </div>
      <p className="text-[#4A5568] mt-3 max-w-2xl">
        {pick(HEAD.intro)}
      </p>

      <div className="mt-10 grid md:grid-cols-2 gap-5">
        {MODES.map((m) => (
          <Link
            key={m.to}
            to={m.to}
            data-testid={m.testId}
            className="app-card p-6 hover:border-[#E5276A] transition-all group block"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#FCE4ED] flex items-center justify-center shrink-0 group-hover:bg-[#E5276A] transition-colors">
                <m.icon className="w-6 h-6 text-[#E5276A] group-hover:text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="font-display text-2xl text-[#1F3A5F] leading-tight">{pick(m.title)}</h2>
                <p className="text-sm text-[#4A5568] mt-2 leading-relaxed">{pick(m.desc)}</p>
                <div className="mt-4 flex items-center justify-between gap-2">
                  <span className="text-[11px] uppercase tracking-widest text-[#E5276A] font-bold">{pick(m.cost)}</span>
                  <ChevronRight className="w-4 h-4 text-[#8B95A1] group-hover:text-[#E5276A]" />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {!user?.subscription_active && user?.role !== "admin" && (
        <div className="mt-10" data-testid="learn-upgrade-nudge">
          <UpgradeNudge featureKey="subscription" reason="locked" variant="card"
            title={pick(HEAD.upgradeTitle)}
            message={pick(HEAD.upgradeMsg)}
          />
        </div>
      )}
    </div>
  );
}
